import type { AppState, VectorLayerModel, WorkScope } from '../core/types';
import type { ThemeTargetConfig, ThemeTargetSource } from '../presentation/config';
import { deriveDisplaySet, featureIdsForLayerScope, layerHasCasurScopeKeys } from './displaySet';
import { selectedFragmentIds } from './selection';
import { replaceSelectionSet, selectedFragmentIdsForSet } from './selectionSet';
import { workScopeFragmentIds } from './scope';

export function idsForScope(layer: VectorLayerModel, state: AppState, scope: WorkScope): ReadonlySet<string> {
  if (layer.kind === 'master') return workScopeFragmentIds(state.model, scope);
  return featureIdsForLayerScope(layer, state.model, scope, layerHasCasurScopeKeys(layer) ? 'attributes' : 'spatial');
}

export function captureThemeTarget(
  state: AppState,
  layer: VectorLayerModel,
  source: ThemeTargetSource,
  scope: WorkScope,
  referenceId?: string,
): ThemeTargetConfig {
  let featureIds: ReadonlySet<string>;
  if (source === 'layer') featureIds = new Set(layer.features.map(({ id }) => id));
  else if (source === 'saved-selection') {
    const saved = state.savedSelections.find((selection) => selection.id === referenceId && selection.layerId === layer.id) ?? [...state.savedSelections].reverse().find((selection) => selection.layerId === layer.id);
    const savedSet = replaceSelectionSet(saved?.entries ?? []);
    featureIds = layer.kind === 'master' ? selectedFragmentIdsForSet(state.model, savedSet) : new Set(saved?.entries.map(({ id }) => id) ?? []);
  } else if (source === 'selection') {
    if (layer.kind === 'master') { const selected = selectedFragmentIdsForSet(state.model, state.selectionSet); featureIds = selected.size ? selected : selectedFragmentIds(state.model, state.selection); }
    else featureIds = new Set(state.selectionSet.layerId === layer.id ? state.selectionSet.entries.map(({ id }) => id) : state.genericSelection?.layerId === layer.id ? [state.genericSelection.featureId] : []);
  } else if (source === 'filter') featureIds = deriveDisplaySet(state).layerFeatureIds.get(layer.id) ?? new Set();
  else featureIds = idsForScope(layer, state, scope);
  return { source, scope, featureIds: [...featureIds], ...(referenceId ? { referenceId } : {}) };
}

export function themeTargetIds(layer: VectorLayerModel, target: ThemeTargetConfig): ReadonlySet<string> {
  const allowed = new Set(layer.features.map(({ id }) => id));
  return new Set(target.featureIds.filter((id) => allowed.has(id)));
}

const visibleTargetCache = new WeakMap<ReadonlySet<string>, WeakMap<ThemeTargetConfig, WeakMap<VectorLayerModel, ReadonlySet<string>>>>();

/**
 * Resolves ThemeTarget without mutating it, then intersects it with the current derived display universe.
 * Memoized by (DisplaySet ids, ThemeTarget, layer) so every consumer of one state shares one stable Set.
 */
export function visibleThemeTargetIds(state: AppState, layer: VectorLayerModel): ReadonlySet<string> {
  const visible = deriveDisplaySet(state).layerFeatureIds.get(layer.id);
  const target = state.classificationConfig.themeTarget;
  if (!visible) return new Set();
  const cached = visibleTargetCache.get(visible)?.get(target)?.get(layer);
  if (cached) return cached;
  const result: ReadonlySet<string> = new Set([...themeTargetIds(layer, target)].filter((id) => visible.has(id)));
  const byTarget = visibleTargetCache.get(visible) ?? new WeakMap<ThemeTargetConfig, WeakMap<VectorLayerModel, ReadonlySet<string>>>();
  const byLayer = byTarget.get(target) ?? new WeakMap<VectorLayerModel, ReadonlySet<string>>();
  byLayer.set(layer, result); byTarget.set(target, byLayer); visibleTargetCache.set(visible, byTarget);
  return result;
}

export function remainderIds(visibleIds: ReadonlySet<string>, targetIds: ReadonlySet<string>): ReadonlySet<string> {
  return new Set([...visibleIds].filter((id) => !targetIds.has(id)));
}
